import * as Dialog from '@radix-ui/react-dialog';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ArrowRight, Check, CircleAlert, GitBranch, Play, X } from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import type { FeatureRunDTO, GateDTO, PushPreviewDTO, RepoProposalDTO, TicketPreviewDTO } from '@harness/shared';
import { Check2, GithubIcon, Modal, Spinner, Switch } from '../../components/ui';
import { api, ApiError, errorText } from '../../lib/api';
import { qk } from '../../lib/keys';
import { useIntegrations, useIssues, useSession, type ProjectDetail } from '../../lib/queries';
import { featureAction, toast, ui, useUI } from '../../lib/store';

// ── actions shared by gate cards and the Feature tab ────────────────────────

function approveWithHash(featureId: string, kind: 'transition' | 'plan') {
  return featureAction(featureId, async () => {
    const p = await api.get<{ previewHash: string }>(`/api/features/${featureId}/previews/${kind}`);
    await api.post(`/api/features/${featureId}/previews/${kind}/approve`, { previewHash: p.previewHash });
  });
}
const post = (featureId: string, path: string) => featureAction(featureId, () => api.post(path));

let noteRequest: ((r: { featureId: string; kind: 'plan' | 'push'; agent: string }) => void) | null = null;
const askForChanges = (featureId: string, kind: 'plan' | 'push', agent: string) => noteRequest?.({ featureId, kind, agent });

export function gateActions(gate: GateDTO, _project: ProjectDetail): { primary: () => Promise<unknown>; secondary?: () => Promise<unknown> } {
  const id = gate.featureRunId;
  if (!id) return { primary: async () => {} };
  switch (gate.kind) {
    case 'transition':
      return { primary: () => approveWithHash(id, 'transition'), secondary: () => post(id, `/api/features/${id}/previews/transition/skip`) };
    case 'plan':
      return { primary: () => approveWithHash(id, 'plan'), secondary: async () => askForChanges(id, 'plan', 'planner') };
    case 'push':
      return { primary: async () => ui.set({ modal: { kind: 'push', featureId: id } }) };
    case 'ticket':
      return { primary: async () => ui.set({ modal: { kind: 'ticket', featureId: id } }), secondary: () => post(id, `/api/features/${id}/previews/ticket/skip`) };
    case 'error':
      return {
        primary: () => post(id, `/api/features/${id}/retry`),
        secondary: async () => {
          if (confirm('Cancel this feature run? Branches stay on the server.')) await post(id, `/api/features/${id}/cancel`);
        },
      };
  }
}

export async function runPrimaryAction(run: FeatureRunDTO, _project: ProjectDetail) {
  switch (run.state) {
    case 'awaiting_transition':
      return approveWithHash(run.id, 'transition');
    case 'awaiting_plan':
      return approveWithHash(run.id, 'plan');
    case 'awaiting_push':
      return ui.set({ modal: { kind: 'push', featureId: run.id } });
    case 'awaiting_ticket':
      return ui.set({ modal: { kind: 'ticket', featureId: run.id } });
    case 'failed':
      return post(run.id, `/api/features/${run.id}/retry`);
  }
}

export default function FeatureDialogs({ project }: { project: ProjectDetail }) {
  const repoPick = useUI((s) => s.repoPick);
  const modal = useUI((s) => s.modal);
  const [note, setNote] = useState<{ featureId: string; kind: 'plan' | 'push'; agent: string } | null>(null);
  useEffect(() => {
    noteRequest = setNote;
    return () => {
      noteRequest = null;
    };
  }, []);
  return (
    <>
      {repoPick && repoPick.projectId === project.id && <RepoPick project={project} issueKey={repoPick.issueKey} onClose={() => ui.set({ repoPick: null })} />}
      {modal?.kind === 'push' && <PushPreview featureId={modal.featureId} onClose={() => ui.set({ modal: null })} onRequestChanges={() => setNote({ featureId: modal.featureId, kind: 'push', agent: 'coder' })} />}
      {(modal?.kind === 'ticket' || modal?.kind === 'transition') && <TicketPreview featureId={modal.featureId} kind={modal.kind} onClose={() => ui.set({ modal: null })} />}
      {note && <ChangesNote {...note} onClose={() => setNote(null)} />}
    </>
  );
}

// ── repo selection ──────────────────────────────────────────────────────────

function branchFor(template: string, key: string, summary: string) {
  const slug = summary
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .split('-')
    .slice(0, 5)
    .join('-');
  return template.replace('{issueKey}', key).replace('{slug}', slug);
}

function RepoPick({ project, issueKey, onClose }: { project: ProjectDetail; issueKey: string; onClose: () => void }) {
  const qc = useQueryClient();
  const nav = useNavigate();
  const integ = useIntegrations(project.id);
  const issues = useIssues(project.id, true);
  const issue = issues.data?.find((i) => i.key === issueKey);
  const proposal = useQuery({
    queryKey: ['proposal', project.id, issueKey],
    queryFn: () => api.get<RepoProposalDTO>(`/api/projects/${project.id}/repos/proposal?issueKey=${encodeURIComponent(issueKey)}`),
    retry: false,
  });
  const repos = integ.data?.repos ?? [];
  const [pick, setPick] = useState<Record<string, boolean> | null>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (pick || !proposal.data) return;
    setPick(Object.fromEntries(repos.map((r) => [r.id, proposal.data.repoIds.includes(r.id)])));
  }, [proposal.data, repos, pick]);
  const sel = pick ?? {};
  const branch = branchFor(project.branchTemplate || 'feature/{issueKey}-{slug}', issueKey, issue?.summary ?? '');
  const chosen = repos.filter((r) => sel[r.id]).map((r) => r.id);

  const start = async () => {
    setBusy(true);
    try {
      const run = await api.post<FeatureRunDTO>(`/api/projects/${project.id}/features`, { issueKey, repoIds: chosen });
      qc.setQueryData(qk.feature(run.id), run);
      await qc.invalidateQueries({ queryKey: qk.conversations(project.id) });
      await qc.invalidateQueries({ queryKey: qk.features(project.id) });
      onClose();
      nav(`/p/${project.slug}/c/${run.conversationId}`);
      setTimeout(() => window.dispatchEvent(new CustomEvent('harness:ctx-tab', { detail: 'feature' })), 60);
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal open onClose={onClose} width={540} label={`Start feature ${issueKey}`}>
      <div className="kicker">Start feature · {issueKey}</div>
      <div className="dialog-title">Which repos does this task touch?</div>
      <div className="dialog-body">
        {issue ? <b style={{ fontWeight: 600 }}>{issue.summary}. </b> : null}
        {proposal.data?.reason ?? 'Proposed from the ticket.'} Branches are cut from each repo’s base after a fresh fetch.
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {repos.length === 0 && (
          <div className="empty">
            No repos linked to this project. Link them in GitHub &amp; Jira settings first.
          </div>
        )}
        {repos.map((r) => (
          <button
            key={r.id}
            onClick={() => setPick({ ...sel, [r.id]: !sel[r.id] })}
            className="repo-pick"
            aria-pressed={!!sel[r.id]}
          >
            <Check2 on={!!sel[r.id]} />
            <span style={{ minWidth: 0 }}>
              <span style={{ display: 'block', font: '800 14px var(--font-heading)' }}>
                {r.owner}/{r.repo}
              </span>
              <span className="mono muted ellipsis" style={{ display: 'block', fontSize: 12 }}>
                {branch} ← {r.baseBranch}
              </span>
            </span>
            <span className="tag tag-neutral">{r.roleLabel || 'repo'}</span>
          </button>
        ))}
      </div>
      <div className="dialog-actions">
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" disabled={busy || chosen.length === 0} onClick={start}>
          {busy ? <Spinner /> : <Play className="icon" />}
          Start feature run
        </button>
      </div>
    </Modal>
  );
}

// ── request changes ─────────────────────────────────────────────────────────

function ChangesNote({ featureId, kind, agent, onClose }: { featureId: string; kind: 'plan' | 'push'; agent: string; onClose: () => void }) {
  const [text, setText] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      await api.post(`/api/features/${featureId}/previews/${kind}/reject`, { note: text.trim() });
      ui.set({ modal: null });
      onClose();
      toast(`Sent to @${agent}`);
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open onClose={onClose} width={520} label="Request changes">
      <div className="kicker">Request changes · @{agent}</div>
      <div className="dialog-title">{kind === 'plan' ? 'What should the plan change?' : 'What should the Coder change before the push?'}</div>
      <textarea className="input" autoFocus value={text} onChange={(e) => setText(e.target.value)} placeholder={kind === 'plan' ? 'e.g. Also add a feature flag, and skip the web changes for now.' : 'e.g. Rename the helper and add a test for empty exports.'} style={{ minHeight: 120 }} />
      <div className="dialog-actions">
        <button className="btn btn-ghost" onClick={onClose}>
          Cancel
        </button>
        <button className="btn btn-primary" disabled={busy || !text.trim()} onClick={send}>
          Send to @{agent}
          {busy ? <Spinner /> : <ArrowRight className="icon" />}
        </button>
      </div>
    </Modal>
  );
}

// ── push preview ────────────────────────────────────────────────────────────

function PushPreview({ featureId, onClose, onRequestChanges }: { featureId: string; onClose: () => void; onRequestChanges: () => void }) {
  const q = useQuery({ queryKey: ['preview', featureId, 'push'], queryFn: () => api.get<PushPreviewDTO>(`/api/features/${featureId}/previews/push`), staleTime: 0, gcTime: 0 });
  const [repoIdx, setRepoIdx] = useState(0);
  const [fileIdx, setFileIdx] = useState(0);
  const [commit, setCommit] = useState<Record<string, string>>({});
  const [prTitle, setPrTitle] = useState<Record<string, string>>({});
  const [draftPr, setDraftPr] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  const p = q.data;
  const repo = p?.repos[repoIdx];
  const file = repo?.files[fileIdx] ?? repo?.files[0];
  const stats = (r: PushPreviewDTO['repos'][number]) => {
    const a = r.files.reduce((x, f) => x + f.additions, 0);
    const d = r.files.reduce((x, f) => x + f.deletions, 0);
    return { a, d, label: `${r.files.length} file${r.files.length === 1 ? '' : 's'} · +${a} −${d}` };
  };
  const pending = repo?.commits.find((c) => !c.sha);
  const firstCommit = repo ? (commit[repo.repoId] ?? (pending ?? repo.commits[repo.commits.length - 1])?.message ?? '') : '';

  const approve = async () => {
    if (!p) return;
    setBusy('approve');
    try {
      await featureAction(featureId, () =>
        api.post(`/api/features/${featureId}/previews/push/approve`, {
          previewHash: p.previewHash,
          edits: { commitMessages: commit, prTitles: prTitle, draftPr },
        }),
      );
      onClose();
      toast('Approved · pushing to GitHub');
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        toast('The agent changed something after this preview — review the new one', 'error');
        void q.refetch();
      } else toast(errorText(e), 'error');
    } finally {
      setBusy(null);
    }
  };
  const discard = async () => {
    setBusy('discard');
    try {
      await api.post(`/api/features/${featureId}/previews/push/discard`);
      onClose();
      toast('Push discarded · branches kept locally');
    } catch (e) {
      toast(errorText(e), 'error');
    } finally {
      setBusy(null);
    }
  };

  const totalRepos = p?.repos.length ?? 0;
  return (
    <Dialog.Root open onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="dialog-backdrop" />
        <Dialog.Content aria-describedby={undefined} style={{ position: 'fixed', inset: 24, zIndex: 51, display: 'flex', justifyContent: 'center' }}>
          <div className="push-frame">
            <div className="modal-head">
              <div style={{ flex: 1, minWidth: 0 }}>
                <div className="kicker">Gate · Push preview</div>
                <Dialog.Title className="modal-title">Review before anything reaches GitHub</Dialog.Title>
              </div>
              {p && <span className="tag tag-outline mono">preview #{p.previewHash.slice(0, 6)}</span>}
              <button className="btn btn-icon" onClick={onClose} title="Close" aria-label="Close">
                <X className="icon" style={{ fontSize: 18 }} />
              </button>
            </div>
            {q.isLoading && (
              <div style={{ flex: 1, display: 'grid', placeItems: 'center' }}>
                <span className="muted" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <Spinner /> Building the diff on the server…
                </span>
              </div>
            )}
            {q.error && <div className="empty err">{errorText(q.error)}</div>}
            {p && repo && (
              <>
                <div className="repo-tabs" role="tablist">
                  {p.repos.map((r, i) => (
                    <button
                      key={r.repoId}
                      role="tab"
                      aria-selected={i === repoIdx}
                      className="repo-tab"
                      onClick={() => {
                        setRepoIdx(i);
                        setFileIdx(0);
                      }}
                    >
                      <GithubIcon />
                      <span>
                        <span className="n">{r.name}</span>
                        <span style={{ fontSize: 11, opacity: 0.8 }}>{stats(r).label}</span>
                      </span>
                    </button>
                  ))}
                </div>
                <div className="push-body">
                  <div className="push-side">
                    <div className="push-branch">
                      <div className="mono" style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                        <GitBranch className="icon" />
                        <b className="ellipsis">{repo.branch}</b>
                      </div>
                      <div className="muted">
                        into {repo.base} · {repo.commits.length} commit{repo.commits.length === 1 ? '' : 's'}
                        {repo.fetchedAt ? ` · fetched ${new Date(repo.fetchedAt).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}` : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                      <div className="micro">Checks from last run</div>
                      {repo.checks.length === 0 && <div className="faint" style={{ fontSize: 13 }}>No test or lint run recorded</div>}
                      {repo.checks.map((c) => (
                        <div key={c.label} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13 }}>
                          {c.ok ? <Check className="icon" /> : <CircleAlert className="icon" style={{ color: 'var(--color-accent-700)' }} />}
                          <span style={{ flex: 1, fontWeight: 700 }}>{c.label}</span>
                          <span className="muted" style={{ fontSize: 12, color: c.ok ? undefined : 'var(--color-accent-700)' }}>
                            {c.value}
                          </span>
                        </div>
                      ))}
                    </div>
                    <div className="micro" style={{ paddingTop: 4 }}>
                      Files · {stats(repo).label}
                    </div>
                    <div className="file-list">
                    {repo.files.map((f, i) => (
                      <button key={f.path} className="file-row" aria-selected={f === file} onClick={() => setFileIdx(i)}>
                        <span style={{ fontWeight: 800, color: f.status === 'A' ? 'var(--diff-add-fg)' : f.status === 'D' ? 'var(--color-accent-700)' : 'var(--color-neutral-800)' }}>{f.status}</span>
                        <span className="path" title={f.path}>
                          {f.path}
                        </span>
                        <span className="mono" style={{ fontSize: 11 }}>
                          <span style={{ color: 'var(--diff-add-fg)' }}>+{f.additions}</span> <span style={{ color: 'var(--color-accent-700)' }}>−{f.deletions}</span>
                        </span>
                      </button>
                    ))}
                    </div>
                    {repo.files.length === 0 && <div className="empty">No changes in this repo.</div>}
                  </div>
                  <div className="diff-pane">
                    {file && <div className="diff-path">{file.path}</div>}
                    {file && <Diff lines={file.diff} />}
                    <div className="push-form">
                      <div className="field">
                        <label>
                          Commit message · {pending ? 'new commit for uncommitted changes' : `latest of ${repo.commits.length}`}
                        </label>
                        <input className="input mono" value={firstCommit} onChange={(e) => setCommit({ ...commit, [repo.repoId]: e.target.value })} />
                      </div>
                      <div className="field">
                        <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <Switch on={draftPr} onChange={setDraftPr} label="Open a draft pull request" />
                          Open a draft pull request
                        </label>
                        <input className="input" value={prTitle[repo.repoId] ?? repo.prTitle} disabled={!draftPr} onChange={(e) => setPrTitle({ ...prTitle, [repo.repoId]: e.target.value })} aria-label="Pull request title" />
                      </div>
                    </div>
                  </div>
                </div>
              </>
            )}
            <div className="modal-foot">
              <span style={{ flex: 1, fontSize: 12, minWidth: 200 }} className="muted">
                Pushes {totalRepos} branch{totalRepos === 1 ? '' : 'es'} to {totalRepos} repo{totalRepos === 1 ? '' : 's'}. Any change by the agent after this point invalidates the preview.
              </span>
              <button className="btn btn-ghost" onClick={discard} disabled={!!busy}>
                Discard
              </button>
              <button className="btn btn-secondary" onClick={onRequestChanges} disabled={!!busy}>
                Request changes
              </button>
              <button className="btn btn-primary btn-lg" onClick={approve} disabled={!!busy || !p}>
                Approve and push
                {busy === 'approve' ? <Spinner /> : <ArrowRight className="icon" />}
              </button>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

function Diff({ lines }: { lines: PushPreviewDTO['repos'][number]['files'][number]['diff'] }) {
  const shown = lines.slice(0, 4000);
  return (
    <div className="diff" role="table" aria-label="Diff">
      {shown.map((l, i) => (
        <div key={i} className={`diff-line ${l.t === '+' ? 'add' : l.t === '-' ? 'del' : l.t === '@' ? 'hunk' : ''}`}>
          <span className="ln">{l.n ?? ''}</span>
          <span className="sg">{l.t === '@' || l.t === ' ' ? '' : l.t}</span>
          <span className="code">{l.s}</span>
        </div>
      ))}
      {lines.length > shown.length && <div className="empty">… {lines.length - shown.length} more lines</div>}
    </div>
  );
}

// ── ticket preview ──────────────────────────────────────────────────────────

/** Renders the Jira-style comment: first line bold, "* " bullets, "repo — PR" rows, "Testing:" line. */
function RenderedComment({ text, author }: { text: string; author: string }) {
  const initials = author
    .split(/\s+/)
    .map((p) => p[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  const blocks = useMemo(() => text.split(/\n{2,}/), [text]);
  return (
    <div className="jira-comment">
      <span className="avatar" style={{ width: 34, height: 34, fontSize: 12 }}>
        {initials}
      </span>
      <div style={{ flex: 1, fontSize: 14, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 }}>
        <div style={{ fontSize: 12 }} className="muted">
          <b style={{ color: 'var(--color-text)' }}>{author}</b> via Harness · just now
        </div>
        {blocks.map((b, i) => {
          const ls = b.split('\n');
          if (ls.every((l) => /^\s*[*-] /.test(l)))
            return (
              <ul key={i} style={{ margin: 0, paddingLeft: 18 }}>
                {ls.map((l, j) => (
                  <li key={j}>{linkify(l.replace(/^\s*[*-] /, ''))}</li>
                ))}
              </ul>
            );
          if (i === 0) return <div key={i} style={{ font: '800 15px var(--font-heading)', whiteSpace: 'pre-wrap' }}>{linkify(b)}</div>;
          return (
            <div key={i} style={{ whiteSpace: 'pre-wrap', fontSize: 13, overflowWrap: 'anywhere' }}>
              {linkify(b)}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function linkify(s: string): ReactNode {
  // **bold** spans (the comment uses Jira-style emphasis), then bare URLs.
  const bold = s.split(/\*\*(.+?)\*\*/g);
  if (bold.length > 1) return bold.map((b, i) => (i % 2 ? <b key={i}>{b}</b> : <span key={i}>{linkify(b)}</span>));
  const parts = s.split(/(https?:\/\/\S+)/g);
  return parts.map((p, i) =>
    /^https?:\/\//.test(p) ? (
      <a key={i} href={p} target="_blank" rel="noreferrer">
        {p.replace(/^https?:\/\/(www\.)?/, '')}
      </a>
    ) : (
      p
    ),
  );
}

function TicketPreview({ featureId, kind, onClose }: { featureId: string; kind: 'ticket' | 'transition'; onClose: () => void }) {
  const user = useSession().data?.user;
  const q = useQuery({ queryKey: ['preview', featureId, kind], queryFn: () => api.get<TicketPreviewDTO>(`/api/features/${featureId}/previews/${kind}`), staleTime: 0, gcTime: 0 });
  const p = q.data;
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const comment = text ?? p?.comment ?? '';

  const act = async (what: 'approve' | 'skip') => {
    if (!p) return;
    setBusy(what);
    try {
      await featureAction(featureId, () =>
        what === 'approve'
          ? api.post(`/api/features/${featureId}/previews/${kind}/approve`, { previewHash: p.previewHash, edits: text != null ? { comment: text } : undefined })
          : api.post(`/api/features/${featureId}/previews/${kind}/skip`),
      );
      onClose();
      toast(what === 'approve' ? `${p.issueKey} moved to ${p.toStatus}${p.comment ? ' · comment posted' : ''}` : 'Ticket update skipped');
    } catch (e) {
      toast(errorText(e), 'error');
      if (e instanceof ApiError && e.status === 409) void q.refetch();
    } finally {
      setBusy(null);
    }
  };

  return (
    <Modal open onClose={onClose} width={760} className="dialog" label="Ticket preview">
      <div style={{ margin: -28, display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <div className="modal-head">
          <div style={{ flex: 1 }}>
            <div className="kicker">Gate · Ticket preview</div>
            <div className="modal-title">{p ? `Update ${p.issueKey} in Jira` : 'Loading…'}</div>
          </div>
          <button className="btn btn-icon" onClick={onClose} title="Close" aria-label="Close">
            <X className="icon" style={{ fontSize: 18 }} />
          </button>
        </div>
        {q.isLoading && (
          <div className="empty">
            <Spinner />
          </div>
        )}
        {q.error && <div className="empty err">{errorText(q.error)}</div>}
        {p && (
          <>
            <div className="ticket-cells">
              <div className="tile tile-blue">
                <div className="tile-label">Status transition</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 6 }}>
                  <span className="tag tag-neutral">{p.fromStatus}</span>
                  <ArrowRight className="icon" />
                  <span className="tag tag-accent">{p.toStatus}</span>
                </div>
              </div>
              <div className="tile tile-neutral">
                <div className="tile-label faint">Field changes</div>
                <div style={{ fontSize: 13, marginTop: 6 }}>{p.fieldChanges || 'None'}</div>
              </div>
            </div>
            <div style={{ padding: '20px 24px' }}>
              <div className="micro" style={{ marginBottom: 8 }}>
                {p.comment != null ? 'Comment, as Jira will show it' : 'Comment'}
              </div>
              {p.comment == null ? (
                <div className="muted" style={{ fontSize: 13 }}>
                  No comment is posted with this update.
                </div>
              ) : editing ? (
                <textarea className="input mono" value={comment} onChange={(e) => setText(e.target.value)} style={{ minHeight: 260, fontSize: 12.5 }} aria-label="Comment text" />
              ) : (
                <RenderedComment text={comment} author={p.author || user?.name || 'You'} />
              )}
            </div>
          </>
        )}
        <div className="modal-foot">
          <span style={{ flex: 1 }} />
          <button className="btn btn-ghost" onClick={() => act('skip')} disabled={!!busy || !p}>
            Skip this update
          </button>
          {p?.comment != null && (
            <button className="btn btn-secondary" onClick={() => setEditing(!editing)} disabled={!!busy}>
              {editing ? 'Preview' : 'Edit'}
            </button>
          )}
          <button className="btn btn-primary btn-lg" onClick={() => act('approve')} disabled={!!busy || !p}>
            Approve and update
            {busy === 'approve' ? <Spinner /> : <ArrowRight className="icon" />}
          </button>
        </div>
      </div>
    </Modal>
  );
}
